// ---------------------------------------------------------------------------

import {
  createActionConsumerCatalogReader,
  listRuntimeActionAliases,
  RUNTIME_ACTION_CONSUMER_CATALOG,
} from '@cogo/action-registry'
import type { RuntimeActionConsumerCatalogProjection } from '@cogo/schema/action-consumer-catalog'
// Legacy action aliases — QA-4 (STUDIO-LIVE-QA-2026-06-12).
//
// Flows generated by the legacy Flutter-era pipeline (and the canonical-todo
// fixture) carry snake_case action names like `set_state_action`. The web
// canonical runtime-v2 ActionFlowEngine dispatches only canonical
// builtin names plus its projected runtime alias set — every other legacy
// name is silently SKIPPED at dispatch. The studio previously dropped those
// steps onto GenericStepForm with raw snake_case labels.
//
// This module is the single source for:
//   1. resolveLegacyActionAlias — legacy name -> canonical form target +
//      lossless param migration (edit-time only; never bulk).
//   2. legacyRuntimeStatus      — honest "will this name run on web?" answer,
//      SCOPED to the names this module knows. The full runtime executor
//      surface is not browser-importable (RUNTIME_ACTION_GROUNDING covers
//      only the 49 host/adapter actions; the validator's executors∩TBox
//      query needs Oxigraph/Node), so no claim is made for unknown names.
//   3. getActionDisplayInfo     — display-only label humanization for step
//      cards (raw name is demoted to secondary text, never hidden).
//
// EVERY entry is evidence-derived (see `evidence` per entry). Pairs whose
// legacy param shape is NOT losslessly derivable stay on GenericStepForm —
// deferred (with the rest of the #965 frozen canvas legacy list):
//   show_toast_action (formKey 'popup'; no observed legacy param shape),
//   graphql_action, gemini_action, supabase_{select,insert,update,delete}_action,
//   expression_action, go_back_action, open_link_action, show_popup_action,
//   close_popup_action, open_drawer_action, close_drawer_action,
//   set_property_action, refresh_action, form_{validate,submit,reset}_action,
//   run_action_flow_action, delay_action (params {duration,unit} ARE
//   losslessly derivable — runtime actionDelay reads `unit` with s/m
//   multipliers — but no dedicated canonical delay form exists), break_action,
//   login_action, logout_action, register_action, console_log_action,
//   storage_{get,set,remove}_action, hash_action, encrypt_action,
//   decrypt_action.
// Runtime alias growth never originates here: runtime-v2 dispatch is execution
// authority, with executor TTL and the generated action registry as projections.
// The retired cogo-ui skill and validator action-types JSON are not alias sources.
// ---------------------------------------------------------------------------

export interface LegacyActionAlias {
  /** Canonical @cogo/action-registry ACTION_DEFS name (gate-tested non-deprecated). */
  canonicalAction: string
  /** Humanized display label for the canonical action. */
  canonicalLabel: string
  /**
   * Pure, idempotent param migration legacy -> canonical. Loses no values:
   * renames documented in `paramRule`, everything else passes through.
   */
  migrateParams: (params: Record<string, unknown>) => Record<string, unknown>
  /** Human-readable param-shape rule for this pair. */
  paramRule: string
  /** Evidence the mapping is derived from (files / observed shapes). */
  evidence: string[]
}

/**
 * Runtime dispatch aliases that DO execute on web, derived from the reviewed
 * runtime consumer projection. Every entry dispatches to a live builtin since
 * the ghost-executor fix.
 * retargeted restful_data_action from the never-registered `restful_data`
 * to `restApi` (which also read-tolerates the legacy responseKey param).
 *
 * Exported for the runtime drift-lock test, which executes every name in
 * this set AND every LEGACY_ACTION_ALIASES key through the REAL
 * ActionFlowEngine and asserts the executed-vs-skipped claim — so the chip
 * fails loudly the day runtime-v2 gains (or loses) an alias/executor here.
 */
export const RUNTIME_EXECUTABLE_ALIASES: ReadonlySet<string> = new Set(
  listRuntimeActionAliases(),
)

export function runtimeExecutableAliasStatus(
  action: string,
  catalog: RuntimeActionConsumerCatalogProjection = RUNTIME_ACTION_CONSUMER_CATALOG,
): 'aliased' | 'skipped' {
  return createActionConsumerCatalogReader(catalog)
    .listRuntimeActionAliases()
    .includes(action)
    ? 'aliased'
    : 'skipped'
}

// ---------------------------------------------------------------------------
// Frozen canvas legacy action inventory (#965).
//
// The Flutter-era canvas insertion surfaces (action-node quick menu +
// action-palette categories) still offer these snake_case `*_action` names.
// flow-bridge nodeToStep serializes them verbatim into flow.cogo.json, and the
// canonical web runtime-v2 ActionFlowEngine recognizes NONE of them as a
// builtin or ACTION_NAME_ALIASES entry — so each is silently SKIPPED at
// dispatch (exception: restful_data_action, which the runtime aliases to the
// restApi builtin). This is the canonical source for that inventory: the
// add-step-popover parity gate imports it (de-dup), and DEFERRED_SKIP_ACTIONS
// is seeded from it.
//
// DO NOT ADD to this list: new canvas entries must use canonical ACTION_DEFS
// names. A frozen entry that gets registered/renamed must leave the list (the
// add-step-popover no-rot ratchet enforces both directions).
// ---------------------------------------------------------------------------
export const FROZEN_CANVAS_LEGACY_ACTIONS = new Set<string>([
  // Data Fetch
  'restful_data_action', 'graphql_action', 'supabase_select_action', 'gemini_action',
  // Data Mutate
  'supabase_insert_action', 'supabase_update_action', 'supabase_delete_action',
  'set_state_action', 'expression_action',
  // Navigation
  'navigation_action', 'go_back_action', 'open_link_action',
  // UI Feedback
  'show_toast_action', 'show_popup_action', 'close_popup_action',
  'open_drawer_action', 'close_drawer_action', 'set_property_action', 'refresh_action',
  // Form
  'form_validate_action', 'form_reset_action', 'form_submit_action',
  // Flow Control (basic-step subset — conditional/loop/switch insert structural nodes)
  'run_action_flow_action', 'delay_action', 'break_action',
  // Auth
  'login_action', 'logout_action', 'register_action',
  // Utility
  'console_log_action', 'storage_get_action', 'storage_set_action',
  'storage_remove_action', 'hash_action', 'encrypt_action', 'decrypt_action',
])

export const LEGACY_ACTION_ALIASES: Record<string, LegacyActionAlias> = {
  set_state_action: {
    canonicalAction: 'setState',
    canonicalLabel: 'Set State',
    migrateParams: (params) => {
      // Legacy {key|path, value} -> canonical {target, value}; runtime-v2
      // actionSetState reads STRICT params.target. Stale aliases are dropped
      // so they cannot diverge (same rule SetStateStepForm commits, #972).
      const { key, path, ...rest } = params
      const target = rest.target ?? key ?? path
      return target !== undefined ? { ...rest, target } : rest
    },
    paramRule: '{key|path -> target}; value and other params pass through; stale key/path dropped',
    evidence: [
      'action-registry.ts HARDCODED_REGISTRY set_state_action formKey=setState',
      'runtime-v2 actionSetState reads strict params.target',
      'canonical-flows.ts fixture set_state_action {key, value}',
      'SetStateStepForm #972 key/path alias-read precedent',
    ],
  },
  restful_data_action: {
    canonicalAction: 'restApi',
    canonicalLabel: 'REST API',
    migrateParams: (params) => {
      // url/method/headers/body/timeout are identity (actions.json maps
      // flutter restful_data_action -> restApi with the SAME names). Legacy
      // {responseKey} -> canonical {resultTarget} (RestApiStepForm's
      // alias-read seeds resultTarget from responseKey); wild corpus
      // spellings {endpoint} -> url and {resultKey} -> `_results.`-slotted
      // resultTarget follow the reconcile-corpus-actions.mjs tier-1 rules
      // (the runtime read-tolerates all three, so migration only
      // canonicalizes — it never changes behavior). Stale aliases are
      // dropped so the spellings cannot diverge (same rule as
      // set_state_action's key/path). The `source` datasource subset is
      // deliberately NOT migrated here beyond the rename — it is
      // loadDatasource territory (codemod tier-2 guard) and no-ops on web.
      const { responseKey, resultKey, endpoint, ...rest } = params
      if (endpoint !== undefined && rest.url === undefined) rest.url = endpoint
      else if (endpoint !== undefined) rest.endpoint = endpoint
      const slotted =
        resultKey !== undefined
          ? String(resultKey).startsWith('_results.')
            ? String(resultKey)
            : `_results.${String(resultKey)}`
          : undefined
      const resultTarget = rest.resultTarget ?? responseKey ?? slotted
      return resultTarget !== undefined ? { ...rest, resultTarget } : rest
    },
    paramRule:
      'identity for url/method/headers/body/timeout; {responseKey -> resultTarget}; {endpoint -> url when url absent}; {resultKey -> resultTarget with _results. slot restored}; stale aliases dropped',
    evidence: [
      '@cogo/schema definitions/registry/actions.json restApi.flutter=restful_data_action, same param names in the registry mapping',
      'runtime-v2 ACTION_NAME_ALIASES maps restful_data_action -> restApi (live builtin since the ghost-executor fix)',
      'runtime-v2 actionRestApi reads url/method/headers/body/timeout/resultTarget (+endpoint/responseKey/resultKey read-aliases)',
      'scripts/reconcile-corpus-actions.mjs tier-1 rules: endpoint->url, resultKey->_results.-slotted resultTarget; tier-2 guard defers the source subset',
    ],
  },
  navigation_action: {
    canonicalAction: 'navigate',
    canonicalLabel: 'Navigate',
    migrateParams: (params) => {
      // Legacy {replace} -> canonical {mode: 'replace'|'push'}; the runtime
      // reads `mode`, never `replace`. Legacy data is unvalidated, so the
      // 'true'/'false' STRING forms coerce too; any other-typed `replace`
      // passes through untouched (the runtime ignores it) — nothing is
      // silently dropped. route/params/transition are read by actionNavigate
      // under the same names — pass through.
      const { replace, ...rest } = params
      const coerced =
        replace === true || replace === 'true'
          ? true
          : replace === false || replace === 'false'
            ? false
            : undefined
      if (coerced !== undefined && rest.mode === undefined) {
        return { ...rest, mode: coerced ? 'replace' : 'push' }
      }
      if (replace !== undefined && coerced === undefined) {
        // Unrecognized shape — keep it rather than lose a value.
        return { ...rest, replace }
      }
      return rest
    },
    paramRule:
      '{replace -> mode:"replace"|"push"} (boolean or "true"/"false" string; other-typed replace passes through); route/params/transition pass through',
    evidence: [
      '@cogo/schema definitions/registry/actions.json navigate.flutter=navigation_action params {route,params,replace,transition}',
      'runtime-v2 actionNavigate reads to??route??target, params (query merge), transition, mode',
      'canonical-flows.ts fixture navigation_action {route}',
    ],
  },
  toast_action: {
    canonicalAction: 'showToast',
    canonicalLabel: 'Show Toast',
    // Identity: the F35 phantom-rename (step-inspector makeConditionBranchStubStep)
    // replaced toast_action {message} with showToast {message} verbatim; the
    // canonical-todo fixture's toast_action carries only {message}.
    migrateParams: (params) => ({ ...params }),
    paramRule: 'identity ({message}; description/variant/durationMs pass through if present)',
    evidence: [
      'F35: makeConditionBranchStubStep renamed toast_action {message} -> showToast {message} (commit 00fcb2684)',
      'canonical-flows.ts fixture toast_action {message}',
      'runtime-v2 actionShowToast reads message/description/variant/durationMs',
    ],
  },
}

// ---------------------------------------------------------------------------
// Deferred-skip action names — legacy names that render plausibly in the
// editor (the add-step canvas offers them; ACTION_CATEGORY/EXPECTED_PARAMS in
// step-inspector even dress some with category badges and field hints) but
// SILENTLY SKIP at runtime: the web runtime has no executor and no working
// alias for them. They are NOT in LEGACY_ACTION_ALIASES (no losslessly
// derivable canonical form), so there is no migrate target — they stay on
// GenericStepForm. Their ONLY honest affordance is a visible skip chip.
//
// Seeded from the union of:
//   1. FROZEN_CANVAS_LEGACY_ACTIONS (the `*_action` spellings above), and
//   2. the bare (`_action`-less) spellings the inspector's ACTION_CATEGORY +
//      EXPECTED_PARAMS maps assign badges/hints to — supabase_{select,insert,
//      update,delete} (show_popup_action / open_drawer_action / graphql_action
//      already carry the `_action` suffix and arrive via the frozen set).
// minus the names that DO run on web or have a migrate target:
//   - RUNTIME_EXECUTABLE_ALIASES (esp. restful_data_action -> restApi), and
//   - the LEGACY_ACTION_ALIASES keys (set_state_action / navigation_action /
//     toast_action / restful_data_action — these report 'skipped'/'aliased'
//     via their own resolver entry and own the migrate notice).
//
// EVERY member is pinned to real runtime-v2 skip status by the #978
// drift-lock probe (legacy-action-aliases.test.ts): if runtime-v2 ever gains
// an executor/alias for one, the probe traces 'ok'/'error' and fails loudly so
// the name is removed from this set (and the chip stops over-claiming).
// ---------------------------------------------------------------------------
const DEFERRED_SKIP_EXTRA_BARE = [
  // Bare (suffix-less) inspector ACTION_CATEGORY/EXPECTED_PARAMS keys whose
  // `*_action` twin is already in FROZEN_CANVAS_LEGACY_ACTIONS. The runtime has
  // no `supabase_*` builtin under either spelling.
  'supabase_select', 'supabase_insert', 'supabase_update', 'supabase_delete',
] as const

export const DEFERRED_SKIP_ACTIONS: ReadonlySet<string> = new Set<string>(
  [...FROZEN_CANVAS_LEGACY_ACTIONS, ...DEFERRED_SKIP_EXTRA_BARE].filter(
    (name) => !RUNTIME_EXECUTABLE_ALIASES.has(name) && LEGACY_ACTION_ALIASES[name] === undefined,
  ),
)

/** Resolve a legacy action name to its canonical form mapping (undefined when unknown). */
export function resolveLegacyActionAlias(
  action: string,
): (LegacyActionAlias & { legacyAction: string }) | undefined {
  const entry = LEGACY_ACTION_ALIASES[action]
  return entry ? { ...entry, legacyAction: action } : undefined
}

/**
 * Honest runtime-dispatch status for a LEGACY action name, scoped to the
 * names this module knows:
 *   'aliased'      — runtime-v2 ACTION_NAME_ALIASES routes it to a live builtin.
 *   'skipped'      — a LEGACY_ACTION_ALIASES key with no live executor: silently
 *                    skipped until migrated (it owns a canonical migrate target).
 *   'deferred-skip'— a DEFERRED_SKIP_ACTIONS name: also silently skipped, but
 *                    with NO losslessly-derivable canonical form, so it has no
 *                    migrate target — it stays on GenericStepForm and only gets
 *                    an honest skip chip.
 *   undefined      — not a name this module tracks (canonical or unknown — no claim).
 *
 * Precedence: aliased (executes) wins, then the resolver-keyed 'skipped' (so a
 * name with a migrate target keeps its notice), then 'deferred-skip'. The three
 * tracked partitions are disjoint by construction (DEFERRED_SKIP_ACTIONS
 * excludes RUNTIME_EXECUTABLE_ALIASES and LEGACY_ACTION_ALIASES keys).
 */
export function legacyRuntimeStatus(
  action: string,
): 'skipped' | 'aliased' | 'deferred-skip' | undefined {
  if (runtimeExecutableAliasStatus(action) === 'aliased') return 'aliased'
  // Every resolver entry NOT in RUNTIME_EXECUTABLE_ALIASES was individually
  // verified against the runtime-v2 builtinActions table + registerAction
  // call sites: none has a live executor or alias.
  if (LEGACY_ACTION_ALIASES[action]) return 'skipped'
  if (DEFERRED_SKIP_ACTIONS.has(action)) return 'deferred-skip'
  return undefined
}

/**
 * Display-only label info for a step card. Humanizes BOTH the legacy names
 * and their canonical sides (so a just-migrated step reads the same), always
 * returning the raw action as secondary text — the truth is never hidden.
 */
export function getActionDisplayInfo(action: string): { label: string; raw?: string } {
  const alias = LEGACY_ACTION_ALIASES[action]
  if (alias) return { label: alias.canonicalLabel, raw: action }
  for (const entry of Object.values(LEGACY_ACTION_ALIASES)) {
    if (entry.canonicalAction === action) return { label: entry.canonicalLabel, raw: action }
  }
  return { label: action }
}
